import Box from '@mui/material/Box';
import { type JSX, useEffect, useState } from 'react';

import { DashboardApi } from 'Api/Dashboards/DashboardApi';
import { Alert } from 'Components/Alert/Alert';
import { Button } from 'Components/Buttons/Buttons';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { ChangeEventType, FormFieldSelect } from 'Components/FormField/FormFieldSelect/FormFieldSelect';
import { Modal } from 'Components/Modal/Modal';
import { ICON_CLOSE } from 'Config/Icons';
import { AggregationMode } from 'Models/ComplianceRequirements';

export interface TargetMaturityModalProps {
    hideModal: () => void;
    dashboardApi: DashboardApi;
}

export const TargetMaturityModal = (props: TargetMaturityModalProps): JSX.Element => {
    const { getComplianceMaturityStatusOptions, regulations } = useCachedData();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string>();
    const [failureMessage, setFailureMessage] = useState<string>();
    const [targetMaturity, setTargetMaturity] = useState<string>();

    // Find the first regulation that uses the FFIEC CAT maturity aggregation mode to derive maturity level options.
    const ffiecRegulation = regulations.find((r) => r.aggregation_mode === AggregationMode.FFIEC_CAT);
    const maturityOptions = ffiecRegulation ? getComplianceMaturityStatusOptions(ffiecRegulation.name) : [];

    useEffect(() => {
        const getTargetMaturity = async () => {
            try {
                const response = await props.dashboardApi.getTargetMaturity();
                setTargetMaturity(response.data);
            } catch (error) {
                setFailureMessage(error.message);
            } finally {
                setIsLoading(false);
            }
        };

        getTargetMaturity();
    }, [props.dashboardApi]);

    const handleSelectChange = (value: ChangeEventType): void => {
        setTargetMaturity(value as string);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();
        setIsSubmitting(true);
        setSuccessMessage(undefined);
        setFailureMessage(undefined);

        if (!targetMaturity) {
            setFailureMessage('Select a valid target maturity.');
            setIsSubmitting(false);
            return;
        }

        try {
            await props.dashboardApi.setTargetMaturity({ target_maturity: targetMaturity });
            setSuccessMessage('Target maturity saved.');
        } catch (error) {
            setFailureMessage(error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return <Modal open isLoading closeModal={props.hideModal} header="FFIEC CAT Target Maturity" subHeader="Select the FFIEC CAT maturity level your organization is currently targeting." failureMessage={failureMessage} />;
    }

    return (
        <Modal open header="FFIEC CAT Target Maturity" subHeader="Select the FFIEC CAT maturity level your organization is currently targeting.">
            {successMessage && <Alert type="success">{successMessage}</Alert>}
            {failureMessage && <Alert type="error">{failureMessage}</Alert>}
            <Box component="form" noValidate onSubmit={handleSubmit}>
                <FormFieldSelect formFieldId="targetMaturity" formFieldLabel="Target Maturity" required options={maturityOptions} selectedOption={targetMaturity} handleChange={handleSelectChange} />
                <div className={'modalFormButtonContainer'}>
                    <Button variant="secondary" onClick={props.hideModal} fontAwesomeImage={ICON_CLOSE}>
                        Close
                    </Button>
                    <Button variant="submit" isLoading={isSubmitting} loadingText="Saving...">
                        Save
                    </Button>
                </div>
            </Box>
        </Modal>
    );
};
